import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import { Box, Skeleton, Typography, useTheme } from '@mui/material';
import { useAppSelector } from 'hooks/redux.hooks';
import {
	getBeneficiaryResponseTimeSummaryData,
	getBeneficiaryResponseTimeSummaryLoading,
} from 'state/reducers/balance.reducer';
import { selectTranslations } from 'state/reducers/translation.reducers';

const formatAvgResponseTime = (hours: number | null, t: (key: string) => string) => {
	if (hours === null) return '—';
	if (hours < 1) {
		return `${Math.round(hours * 60)} ${t('page.dashboard.beneficiaryTable.responseTimeMinutes')}`;
	}
	return `${Math.round(hours * 10) / 10} ${t('page.dashboard.beneficiaryTable.responseTimeHours')}`;
};

export const BeneficiaryResponseBox = () => {
	const theme = useTheme();
	const t = useAppSelector(selectTranslations);
	const summary = useAppSelector(getBeneficiaryResponseTimeSummaryData);
	const loading = useAppSelector(getBeneficiaryResponseTimeSummaryLoading);
	const textColor = theme.palette.root.basicDeeoGreen;

	return (
		<Box
			sx={{
				minHeight: 280,
				borderRadius: '8px',
				boxShadow: '0px 0px 16px rgba(0, 58, 53, 0.15)',
				p: 2,
				backgroundColor: theme.palette.background.paper,
			}}
		>
			<Box display="flex" alignItems="center" gap={1}>
				<AccessTimeOutlinedIcon sx={{ color: textColor, fontSize: 20 }} />
				<Typography variant="subtitle2" sx={{ color: textColor }}>
					{t('page.dashboard.beneficiaryResponse.title')}
				</Typography>
			</Box>
			{summary ? (
				<>
					<Box display="flex" alignItems="center" gap={2} my={1}>
						<Typography variant="h4" color={theme.palette.root.forestGreen}>
							{summary.pctWithin24h === null ? '—' : `${summary.pctWithin24h}%`}
						</Typography>
						<Typography variant="captionRegular" sx={{ color: textColor }}>
							{t('page.dashboard.beneficiaryResponse.within24Hours')}
						</Typography>
					</Box>
					{(
						[
							['average', summary.avgHours],
							['approval', summary.avgToApprove],
							['decline', summary.avgToDecline],
						] as const
					).map(([key, value]) => (
						<Box key={key} display="flex" justifyContent="space-between" py={0.75}>
							<Typography variant="captionRegular" sx={{ color: textColor }}>
								{t(`page.dashboard.beneficiaryResponse.${key}`)}
							</Typography>
							<Typography variant="boldSmall" sx={{ color: textColor }}>
								{formatAvgResponseTime(value, t)}
							</Typography>
						</Box>
					))}
				</>
			) : loading ? (
				<Skeleton variant="rectangular" height={210} sx={{ mt: 1.5, borderRadius: 1 }} />
			) : null}
		</Box>
	);
};
